import * as THREE from 'three';
import { groundUnder } from './common.js';

/**
 * Scout Pulse — the bottom rung of the ladder.
 *
 * One sonar ping from the player's position. Everything inside the radius is
 * revealed for a few seconds, whether or not it is in line of sight. It is cheap
 * enough to fire every thirty seconds all match, which is the point: the bottom of
 * a scorestreak ladder has to stay relevant, or the ladder collapses into
 * "grind until gunship".
 *
 * Visually it is a single expanding ring on the deck plus the shared threat
 * markers the system draws — two draw calls for the whole effect.
 */

export const TUNING = {
  radius: 55,
  revealTime: 5.0,
  ringLife: 1.1,
  ringSpeed: 55, // m/s the wavefront travels
};

export class ScoutPulse {
  static id = 'scout_pulse';

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.ring = null;
    this.t = 0;
  }

  _build() {
    if (this.ring) return;
    const geo = new THREE.RingGeometry(0.86, 1.0, 64);
    geo.rotateX(-Math.PI / 2);
    const m = new THREE.MeshBasicMaterial({
      color: 0x6fd6ff,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    this.ring = new THREE.Mesh(geo, m);
    this.ring.frustumCulled = false;
    this.ring.renderOrder = 4;
    this.ring.name = 'streak-pulse-ring';
    this.ring.visible = false;
    this.ctx.scene.add(this.ring);
  }

  deploy() {
    this._build();
    const p = this.ctx.player;
    const x = p?.position?.x ?? 0;
    const z = p?.position?.z ?? 0;
    const y = groundUnder(this.ctx, x, z, (p?.position?.y ?? 2) + 2, 12) + 0.06;
    this.ring.position.set(x, y, z);
    this.ring.visible = true;
    this.t = 0;

    this.sys.revealAll(TUNING.revealTime, 'pulse', TUNING.radius);
    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.9, pitch: 0.7 });
    this.ctx.events.emit('audio:play', { id: 'ui_hit', volume: 0.55, pitch: 0.55, delay: 0.09 });
    return true;
  }

  update(dt) {
    this.t += dt;
    const k = this.t / TUNING.ringLife;
    if (k >= 1) {
      this.ring.visible = false;
      // The reveal outlives the ring; the system owns its timer, so this streak's
      // own lifetime ends here.
      return false;
    }
    const r = this.t * TUNING.ringSpeed;
    this.ring.scale.set(r, 1, r);
    this.ring.material.opacity = 0.75 * (1 - k) * (1 - k);
    return true;
  }

  end() {
    if (this.ring) this.ring.visible = false;
  }

  dispose() {
    if (!this.ring) return;
    this.ring.parent?.remove(this.ring);
    this.ring.geometry.dispose();
    this.ring.material.dispose();
    this.ring = null;
  }
}
